<template>
  <template v-if="fetchDataSources">
    <template
      v-for="[fetch, ds] in Object.entries(fetchDataSources)"
      :key="fetch + ds.map((d) => d.id).join('')"
    >
      <DataSourceFetcher :fetch="(fetch as FetchType)" :dataSources="ds" />
    </template>
  </template>
  <template v-if="!enabledEntityLevels.includes(entityLevel)">
    <DashboardMissingFilter />
  </template>
  <template v-else-if="isLoading">
    <DashboardLoading />
  </template>
  <template v-else-if="isMissingData">
    <DashboardMissingData />
  </template>

  <template v-else>
    <section class="data-section-header">
      <InfoLabel :title="title" :info="info" :size="'mediumBold'" :fullscreen="fullscreen" />
    </section>
    <section class="data-section-content">
      <FeatureChildren feature="home/*/*/*/data/*" />
    </section>
  </template>
</template>
<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, watch } from 'vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import { sharedDataKey } from '../../types'
import DashboardMissingFilter from '../components/DashboardMissingFilter.vue'
import DashboardLoading from './DashboardLoading.vue'
import { useDataSources } from '@/composables/useDataSources'
import DataSourceFetcher from './DataSourceFetcher.vue'
import DashboardMissingData from './DashboardMissingData.vue'
import { FetchType } from '@/fetches'
import FeatureChildren from '@/components/FeatureChildren.vue'
import { DataSource } from './data-section-types'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'
import { storeToRefs } from 'pinia'
import { useInformation } from '@/pinia/information'
import useColoredEntities from '@/composables/useColoredEntities'

type Props = {
  isLoading: boolean
  isMissingData: boolean
  title: string
  info?: string
  informationFetch?: FetchType
  dataSources?: DataSource[]
  enabledEntityLevels?: EntityLevel[]
}

const props = withDefaults(defineProps<Props>(), {
  enabledEntityLevels: () => ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'FLOOR', 'SPACE'],
  info: undefined,
  dataSources: undefined,
  informationFetch: 'fetch.information'
})

const { setDataSource } = useDataSources()
const coloredEntities = useColoredEntities()
const information = useInformation()

const { entityLevel } = storeToRefs(useEntityLevel())

const sharedData = inject(sharedDataKey)

const fullscreen = computed<boolean>(() => sharedData?.fullscreen ?? false)

const fetchDataSources = computed<Record<FetchType, DataSource<'fetch'>[]> | undefined>(() =>
  (
    props.dataSources?.filter((dataSource) => dataSource.type === 'fetch') as
      | DataSource<'fetch'>[]
      | undefined
  )?.reduce((acc, dataSource) => {
    if (!acc[dataSource.fetch]) {
      acc[dataSource.fetch] = []
    }
    acc[dataSource.fetch].push(dataSource)
    return acc
  }, {} as Record<string, DataSource<'fetch'>[]>)
)

watch(
  () => props.dataSources,
  (dataSources) => {
    if (dataSources) {
      dataSources.forEach((dataSource) => {
        if (dataSource.type === 'value') {
          setDataSource({
            id: dataSource.id,
            data: dataSource.value,
            error: false,
            isLoading: false
          })
        }
      })
    }
  },
  { immediate: true }
)

onMounted(() => {
  information.setFetchId(props.informationFetch)
})

onUnmounted(() => {
  coloredEntities.reset(true)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.data-section-header {
  display: flex;
  padding: 2px;
  margin-top: 16px;
  margin-bottom: 16px;
  align-content: flex-start;
  align-items: center;
}
.data-section-content {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  width: 100%;
}

.title-row {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-greyscale-content;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 4px;
}

.legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
}
</style>
